import Titulo from '../../components/tipografia/Titulo';
import styles from './page.module.css';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Descricao from '../../components/tipografia/Descricao';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import BotaoPerigoso from '../../components/BotaoPerigoso';
import VBox from '../../components/layout/VBox';
import CampoTexto from '../../components/Formulario/CampoTexto';
import Badge from '../../components/layout/Badge';
import Icon from '@mdi/react';
import { mdiMagnify, mdiPlus, mdiPen, mdiTrashCan, mdiPause, mdiPlay, mdiCloseCircleOutline } from '@mdi/js';
import { useMutation, useQuery } from 'react-query';
import axios from 'axios';
import React from 'react';
import { useDebounce } from '../chat/useDebounce';
import Skeleton from '../../components/layout/Skeleton';
import { toast } from 'react-toastify';
import Modal from '../../components/layout/Modal';
import AlertBox from '../../components/layout/AlertBox';
import DataTable, { DataTableHeader } from '../../components/layout/DataTable';
import { FormRecorrencia } from './FormRecorrencia';
import { format, parseISO } from 'date-fns';

const frequenciaLabel: Record<string, string> = {
  semanal: 'Semanal',
  mensal: 'Mensal',
  trimestral: 'Trimestral',
  semestral: 'Semestral',
  anual: 'Anual',
};

const statusVariant: Record<string, any> = {
  ativa: 'success',
  pausada: 'warning',
  cancelada: 'secondary',
};

const statusLabel: Record<string, string> = {
  ativa: 'Ativa',
  pausada: 'Pausada',
  cancelada: 'Cancelada',
};

const LancamentoFinanceiroRecorrenciaPage = () => {
  const [search, setSearch] = React.useState<string>('');
  const [selected, setSelected] = React.useState<any>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [showDeleteModal, setShowDeleteModal] = React.useState<number | null>(null);
  const [showFormModal, setShowFormModal] = React.useState<boolean>(false);
  const debouncedSearch = useDebounce(search, 500);

  const query = useQuery(
    ['lancamento_financeiro_recorrencia:search', debouncedSearch],
    async () => {
      const res = await axios.get('/api/lancamento_financeiro_recorrencia', {
        params: { txt: debouncedSearch || undefined, t: 50 },
      });
      return res.data;
    },
  );

  const changeMutation = useMutation(
    async (form: any) => {
      setFieldErrors({});
      if (form.id) {
        return await axios.put(`/api/lancamento_financeiro_recorrencia/${form.id}`, form);
      }
      return await axios.post('/api/lancamento_financeiro_recorrencia', form);
    },
    {
      onSuccess: () => {
        toast('Recorrência salva com sucesso', {
          type: 'success', position: 'top-center', autoClose: 5000,
        });
        query.refetch();
        setShowFormModal(false);
        setSelected(null);
      },
      onError: (error: any) => {
        setFieldErrors(error.response?.data?.fieldErrors || {});
        toast(error.response?.data?.msg ?? 'Erro ao salvar recorrência', {
          type: 'error', position: 'top-center', autoClose: 5000,
        });
      },
    },
  );

  const statusMutation = useMutation(
    async ({ item, status }: { item: any; status: string }) => {
      // Envia o objeto completo (não só { status }): a validação do backend
      // exige todos os campos obrigatórios da recorrência a cada PUT.
      return await axios.put(`/api/lancamento_financeiro_recorrencia/${item.id}`, { ...item, status });
    },
    {
      onSuccess: () => {
        query.refetch();
      },
      onError: (error: any) => {
        toast(error.response?.data?.msg || 'Erro ao alterar a recorrência', {
          type: 'error', position: 'top-center', autoClose: 5000,
        });
      },
    },
  );

  const deleteMutation = useMutation(
    async (id: number) => await axios.delete(`/api/lancamento_financeiro_recorrencia/${id}`),
    {
      onSuccess: () => {
        toast('Recorrência excluída com sucesso', {
          type: 'success', position: 'top-center', autoClose: 5000,
        });
        query.refetch();
        setShowDeleteModal(null);
      },
      onError: (error: any) => {
        toast(error.response?.data?.msg || 'Erro ao excluir recorrência', {
          type: 'error', position: 'top-center', autoClose: 5000,
        });
      },
    },
  );

  const handleOpenForm = (form: any = null) => {
    setSelected(form);
    setShowFormModal(true);
  };

  const handleCloseForm = () => {
    setSelected(null);
    setShowFormModal(false);
  };

  const handleFormConfirm = () => {
    if (selected) {
      changeMutation.mutate(selected);
    }
  };

  const renderContent = () => {
    if (query.isLoading) {
      return (
        <VBox gap="8px">
          <Skeleton format="box" />
          <Skeleton format="box" />
          <Skeleton format="box" />
        </VBox>
      );
    }

    if (query.error) {
      return (
        <AlertBox variant="danger">
          <p>Erro ao carregar recorrências: {(query.error as any)?.message || 'Erro desconhecido'}</p>
        </AlertBox>
      );
    }

    const headers: DataTableHeader[] = [
      { label: 'Descrição', width: '25%', column: 'descricao' },
      {
        label: 'Tipo',
        width: '100px',
        render: (item: any) => (
          <Badge variant={item.tipo === 'receita' ? 'success' : 'tutorial'}>
            {item.tipo === 'receita' ? 'Receita' : 'Despesa'}
          </Badge>
        ),
      },
      {
        label: 'Valor',
        width: '120px',
        alignRight: true,
        render: (item: any) =>
          Number(item.valor ?? 0).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }),
      },
      {
        label: 'Frequência',
        width: '110px',
        render: (item: any) => frequenciaLabel[item.frequencia] ?? item.frequencia,
      },
      {
        label: 'Próxima ocorrência',
        width: '130px',
        render: (item: any) =>
          item.proxima_data_vencimento
            ? format(parseISO(item.proxima_data_vencimento), 'dd/MM/yyyy')
            : '-',
      },
      {
        label: 'Status',
        width: '100px',
        render: (item: any) => (
          <Badge variant={statusVariant[item.status] ?? 'secondary'}>
            {statusLabel[item.status] ?? item.status}
          </Badge>
        ),
      },
      {
        label: 'Ações',
        width: '160px',
        stickRight: true,
        alignRight: true,
        render: (item: any) => (
          <HBox gap="8px" style={{ justifyContent: 'flex-end' }}>
            {item.status === 'ativa' && (
              <Botao
                variant="none-secondary"
                onClick={() => statusMutation.mutate({ item, status: 'pausada' })}
                title="Pausar"
                icon={mdiPause}
              />
            )}
            {item.status === 'pausada' && (
              <Botao
                variant="none-success"
                onClick={() => statusMutation.mutate({ item, status: 'ativa' })}
                title="Retomar"
                icon={mdiPlay}
              />
            )}
            {item.status !== 'cancelada' && (
              <Botao
                variant="none-warning"
                onClick={() => statusMutation.mutate({ item, status: 'cancelada' })}
                title="Cancelar"
                icon={mdiCloseCircleOutline}
              />
            )}
            <Botao variant="none-primary" onClick={() => handleOpenForm(item)} title="Editar" icon={mdiPen} />
            <Botao variant="none-danger" onClick={() => setShowDeleteModal(item.id)} title="Apagar" icon={mdiTrashCan} />
          </HBox>
        ),
      },
    ];

    return <DataTable cabecalhos={headers} linhas={query.data.result} />;
  };

  return (
    <LayoutUsuario>
      <div className={styles.container}>
        <VBox gap="24px">
          <HBox>
            <div className={styles.headerTitulo}>
              <Titulo>Recorrências</Titulo>
              <Descricao>
                Receitas e despesas que se repetem automaticamente (aluguel, assinaturas, folha de pagamento...).
              </Descricao>
            </div>
            <div className={styles.headerAcoes}>
              <Botao
                variant="primary"
                onClick={() => handleOpenForm({
                  tipo: 'despesa',
                  frequencia: 'mensal',
                  dias_antecedencia_geracao: 30,
                })}
                title="Nova recorrência"
                icon={mdiPlus}
              >
                Nova recorrência
              </Botao>
            </div>
          </HBox>

          <CampoTexto
            placeholder="Buscar recorrência..."
            value={search}
            onChange={setSearch}
            prepend={<Icon path={mdiMagnify} size={1} color="var(--tc-color-gray-500)" />}
          />

          <div>{renderContent()}</div>
        </VBox>
      </div>

      <Modal
        instanceId="recorrencia-delete-modal"
        show={!!showDeleteModal}
        onClose={() => setShowDeleteModal(null)}
        size="sm"
        title="Confirmar exclusão"
        cancelProps={{ label: 'Cancelar' }}
        confirmProps={{ label: 'Confirmar', variant: 'danger', disabled: deleteMutation.isLoading }}
        onConfirm={() => showDeleteModal && deleteMutation.mutate(showDeleteModal)}
        onCancel={() => setShowDeleteModal(null)}
      >
        <p>Tem certeza que deseja excluir esta recorrência?</p>
        <p>Os lançamentos já gerados por ela não serão apagados.</p>
      </Modal>

      <Modal
        instanceId="recorrencia-form-modal"
        show={showFormModal}
        onClose={handleCloseForm}
        onCancel={handleCloseForm}
        onConfirm={handleFormConfirm}
        title={selected?.id > 0 ? 'Editar recorrência' : 'Nova recorrência'}
        confirmProps={{ label: 'Salvar', variant: 'primary', disabled: changeMutation.isLoading }}
        cancelProps={{ label: 'Cancelar' }}
      >
        <FormRecorrencia
          value={selected}
          onChange={setSelected}
          fieldErrors={fieldErrors}
        />
      </Modal>
    </LayoutUsuario>
  );
};

export default LancamentoFinanceiroRecorrenciaPage;
